import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Text, Group, Flex, Badge, Paper, Table, Button, Skeleton,
  UnstyledButton, useMantineTheme,
} from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch, IconX } from '@tabler/icons-react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import { notifications } from '@mantine/notifications';

export type AggregateAdKeywordParams = {
  visiopt_campaign_id: string[];
  filter_visiopt_campaign_ids: string[];
  ad_id: string;
  ad_name: string;
  attribution_model: string;
  report_time: string;
  test_variation_enabled?: number;
};

type GoalMeta = { key: string; name: string };
type KeywordRow = {
  keyword: string;
  uniques: number;
  visits: number;
  goals: Record<string, { conversions: number; ucr: string; cr: string; revenue: number }>;
};

export function AggregateAdKeywordDrawer({
  opened,
  onClose,
  params,
  onExport,
}: {
  opened: boolean;
  onClose: () => void;
  params: AggregateAdKeywordParams | null;
  onExport?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const surfaceBg     = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const rowBorder     = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const headerLabelCo = isDark ? theme.colors.dark[1] : theme.colors.gray[6];
  const numericCo     = isDark ? theme.colors.gray[3] : theme.colors.gray[8];

  const [rows, setRows] = useState<KeywordRow[]>([]);
  const [goals, setGoals] = useState<GoalMeta[]>([]);
  const [loading, setLoading] = useState(false);
  // Reopening fast must not let an earlier response overwrite the current one.
  const requestSeq = useRef(0);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [exporting, setExporting] = useState(false);

  // Re-fetch on a different ad or date range, never on a plain re-render.
  const openKey = opened && params
    ? `${params.ad_id}|${params.report_time}|${params.attribution_model}|tv${params.test_variation_enabled ?? 0}`
    : '';

  useEffect(() => {
    if (openKey === '' || !params) { return; }
    setRows([]); setGoals([]); setSortKey(null); setSortDir('asc');
    setLoading(true);
    const seq = ++requestSeq.current;
    AggregatedReportsAPI
      .getadkeyworddrawerdata({
        visiopt_campaign_id: params.visiopt_campaign_id,
        filter_visiopt_campaign_ids: params.filter_visiopt_campaign_ids,
        ad_id: params.ad_id,
        attribution_model: params.attribution_model,
        report_time: params.report_time,
        test_variation_enabled: params.test_variation_enabled ?? 0,
      })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) { navigate('/logout'); }
        return null;
      })
      .then((data) => {
        if (seq !== requestSeq.current) { return; }
        setRows(data && data.status === 1 && Array.isArray(data.data?.keyword_data) ? data.data.keyword_data : []);
        setGoals(data && data.status === 1 && Array.isArray(data.data?.goals) ? data.data.goals : []);
      })
      .catch((e) => console.log(e))
      .finally(() => { if (seq === requestSeq.current) { setLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  /* Goal figures live one level down on each row, so sorting takes a reader that knows how to
     reach them: 'goal:<key>:<metric>' for those, a plain field name for the rest. The goal key is
     itself colon-separated ('webhook:<enc id>'), so it is peeled off the ends rather than split. */
  const readValue = (row: any, key: string) => {
    if (key.startsWith('goal:')) {
      const lastSep = key.lastIndexOf(':');
      const goalKey = key.slice('goal:'.length, lastSep);
      const metric  = key.slice(lastSep + 1);
      const value = row.goals?.[goalKey]?.[metric];
      return metric === 'conversions' ? Number(value) || 0 : parseFloat(value) || 0;
    }
    return row[key];
  };

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDir(dir);
    setRows((prev) => [...prev].sort((a, b) => {
      const av = readValue(a, key);
      const bv = readValue(b, key);
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return dir === 'asc' ? cmp : -cmp;
      }
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
    }));
  };

  /* Totals so the drawer can be checked against the ad row it was opened from. */
  const totals = rows.reduce(
    (acc: any, row) => {
      acc.uniques += Number(row.uniques) || 0;
      acc.visits += Number(row.visits) || 0;
      goals.forEach((goal) => {
        if (!acc.goals[goal.key]) { acc.goals[goal.key] = { conversions: 0, revenue: 0 }; }
        acc.goals[goal.key].conversions += Number(row.goals?.[goal.key]?.conversions) || 0;
        acc.goals[goal.key].revenue += Number(row.goals?.[goal.key]?.revenue) || 0;
      });
      return acc;
    },
    { uniques: 0, visits: 0, goals: {} as Record<string, { conversions: number; revenue: number }> }
  );

  const rate = (part: number, whole: number) => (whole > 0 ? ((part / whole) * 100).toFixed(2) : '0.00');

  // Google only sends {keyword} on Search clicks, so Video, Demand Gen and Display ads land here
  // with an empty keyword. Say so instead of leaving the reader with a bare dash.
  const hasAnyKeyword = rows.some((r) => r.keyword && r.keyword !== '-');

  // Every Keyword View exports the same complete keyword file for the report, not just this view.
  const exportKeywords = () => {
    if (!onExport) { return; }
    setExporting(true);
    onExport()
      .then((fileName) => {
        if (fileName === '') {
          notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'No keyword data available to export.' });
        }
      })
      .catch(() => {
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Could not export the keywords.' });
      })
      .finally(() => setExporting(false));
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortKey !== column) { return <IconSelector size={13} stroke={1.6} color={headerLabelCo} />; }
    return sortDir === 'asc'
      ? <IconChevronUp size={13} stroke={2} color={theme.colors.teal[6]} />
      : <IconChevronDown size={13} stroke={2} color={theme.colors.teal[6]} />;
  };

  const Th = ({ column, label, numeric }: { column: string; label: string; numeric?: boolean }) => (
    <th style={{ padding: '10px 12px', whiteSpace: 'nowrap', textAlign: numeric ? 'right' : 'left' }}>
      <UnstyledButton onClick={() => handleSort(column)} sx={{ width: '100%' }}>
        <Group spacing={4} align="center" position={numeric ? 'right' : 'left'} noWrap>
          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>
            {label}
          </Text>
          <SortIcon column={column} />
        </Group>
      </UnstyledButton>
    </th>
  );

  const num = (v: any) => Number(v || 0).toLocaleString();
  const colCount = 3 + goals.length * 4;

  return (
    <Drawer.Root opened={opened} onClose={onClose} position="right" size="xl" trapFocus={false}>
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header sx={{ paddingBottom: 12, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Group spacing={8} mb={2} noWrap>
              <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Keyword View</Text>
              {params && (
                <Badge size="md" variant="light" color="blue" sx={{ fontWeight: 600 }}>
                  {params.ad_name}
                </Badge>
              )}
            </Group>
            <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>
              Keywords that drove traffic through this ad, with Visiopt results. Google reports spend
              per ad group only, so no spend is shown per keyword here.
            </Text>
            {!loading && rows.length > 0 && !hasAnyKeyword && (
              <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>
                Google sends a keyword only on Search clicks. This ad runs on Video, Demand Gen or
                Display, so its traffic arrives without one and is grouped as a single row.
              </Text>
            )}
          </Box>
          <Group spacing="xs" noWrap>
            <Button size="xs" variant="light" color="teal" onClick={exportKeywords} disabled={!onExport} loading={exporting}>
              Export
            </Button>
            <Drawer.CloseButton />
          </Group>
        </Drawer.Header>

        <Drawer.Body sx={{ paddingTop: 0 }}>
          <Paper withBorder radius="md" sx={{ backgroundColor: 'transparent', borderColor: rowBorder, overflow: 'hidden' }}>
            {loading ? (
              <Flex direction="column" gap="xs" p="sm">
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={34} radius="sm" />)}
              </Flex>
            ) : rows.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={48}>
                <IconSearch size="2rem" stroke={1.4} color={headerLabelCo} />
                <Text size="sm" mt={8} color="dimmed">No keywords found for this ad.</Text>
              </Flex>
            ) : (
              <SimpleBar autoHide={false} style={{ maxHeight: 'calc(100vh - 230px)' }}>
                <Table withColumnBorders sx={{ marginBottom: 0, 'th, td': { borderColor: `${rowBorder} !important` } }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                    <tr>
                      <Th column="keyword" label="Keyword" />
                      <Th column="uniques" label="Uniques" numeric />
                      <Th column="visits" label="Visits" numeric />
                      {goals.map((goal) => (
                        <React.Fragment key={goal.key}>
                          <Th column={`goal:${goal.key}:conversions`} label={`${goal.name} Conv.`} numeric />
                          <Th column={`goal:${goal.key}:ucr`} label={`${goal.name} UCR %`} numeric />
                          <Th column={`goal:${goal.key}:cr`} label={`${goal.name} CR %`} numeric />
                          <Th column={`goal:${goal.key}:revenue`} label={`${goal.name} Revenue`} numeric />
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={`${row.keyword}-${i}`} style={{ borderTop: `1px solid ${rowBorder}` }}>
                        <td style={{ padding: '10px 12px' }}>
                          {row.keyword && row.keyword !== '-' ? (
                            <Text size="sm" sx={{ overflowWrap: 'anywhere' }}>{row.keyword}</Text>
                          ) : (
                            <Text size="sm" color="dimmed" sx={{ fontStyle: 'italic' }}>No keyword</Text>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>{num(row.uniques)}</Text>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>{num(row.visits)}</Text>
                        </td>
                        {goals.map((goal) => {
                          const g: any = row.goals?.[goal.key] || {};
                          return (
                            <React.Fragment key={goal.key}>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>{num(g.conversions)}</Text>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>{g.ucr ?? '0.00'}%</Text>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>{g.cr ?? '0.00'}%</Text>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
                                  ${Number(g.revenue || 0).toFixed(2)}
                                </Text>
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${rowBorder}`, backgroundColor: surfaceBg }}>
                      <td style={{ padding: '10px 12px' }}><Text size="sm" weight={700}>Total</Text></td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{totals.uniques.toLocaleString()}</Text>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{totals.visits.toLocaleString()}</Text>
                      </td>
                      {goals.map((goal) => {
                        const g = totals.goals[goal.key] || { conversions: 0, revenue: 0 };
                        return (
                          <React.Fragment key={goal.key}>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{g.conversions.toLocaleString()}</Text>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{rate(g.conversions, totals.uniques)}%</Text>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{rate(g.conversions, totals.visits)}%</Text>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>${g.revenue.toFixed(2)}</Text>
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tfoot>
                </Table>
              </SimpleBar>
            )}
            {!loading && rows.length === 0 && colCount > 0 && <Box />}
          </Paper>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
